import React from 'react';
import { Card, CardContent, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { ScopeInfo } from '../../services/household-retirement-service';
import { money } from './format';

const OWNER_LABEL = { own: 'theirs', joint: 'joint', other: 'other adult', education: 'education (RESP)' } as const;

/** What a person-scoped plan counted: each account, whose it is, the share
 * taken, and the resulting amount. */
export const ScopePanel: React.FC<{ scope: ScopeInfo; currency?: string }> = ({ scope, currency = 'CAD' }) => {
  const rows = scope.accounts.filter((a) => Math.abs(a.balance) >= 1);
  return (
    <Card data-testid="scope-panel">
      <CardContent>
        <Typography variant="overline" color="text.secondary">What counts as {scope.name}'s</Typography>
        {scope.notes.map((n) => <Typography key={n} variant="body2">{n}</Typography>)}
        <Table size="small" sx={{ mt: 1 }} aria-label="Accounts counted">
          <TableHead>
            <TableRow>
              <TableCell>Account</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell align="right">Share</TableCell>
              <TableCell align="right">Counted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a, i) => (
              <TableRow key={`${a.name}-${i}`} sx={{ opacity: a.share_pct === 0 ? 0.5 : 1 }}>
                <TableCell>{a.name}</TableCell>
                <TableCell>{a.kind.replace('_', ' ')}</TableCell>
                <TableCell><Chip size="small" variant="outlined" label={OWNER_LABEL[a.owner]} /></TableCell>
                <TableCell align="right" className="fw-num">{money(a.balance, currency)}</TableCell>
                <TableCell align="right" className="fw-num">{a.share_pct.toFixed(0)}%</TableCell>
                <TableCell align="right" className="fw-num">{money(a.counted, currency)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {Object.entries(scope.counted).map(([k, v]) => (
            <Typography key={k} variant="caption" color="text.secondary">{k.replace('_', ' ')}: {money(v, currency)}</Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
